import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Pagination from '../components/Pagination';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import ManaCost from '../components/ManaCost';
import './WishListPage.css';

const API        = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;
const IMAGE_BASE = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api/images`;

interface WishListItem {
  wishListId:  number;
  cardId:      string;
  notes:       string | null;
  addedDate:   string;
  name:        string;
  setCode:     string;
  setName:     string;
  rarity:      string;
  manaCost:    string;
  typeLine:    string;
  colors:      string;
  borderColor: string;
  reserved:    boolean;
  price:       number;
  imageSmall:  string | null;
  imageNormal: string | null;
}

type SortKey = 'added_desc' | 'added_asc' | 'name' | 'name_desc' | 'price_desc' | 'price_asc' | 'rarity';
type ViewMode = 'grid' | 'list';

const RARITY_COLORS: Record<string, string> = {
  mythic: '#e8954a', rare: '#c8a45a', uncommon: '#8ab4c8', common: '#9a9aaa',
};

const FORMAT_OPTIONS = [
  { value: '', label: 'All Formats' },
  { value: 'standard',  label: 'Standard'  },
  { value: 'pioneer',   label: 'Pioneer'   },
  { value: 'modern',    label: 'Modern'    },
  { value: 'legacy',    label: 'Legacy'    },
  { value: 'vintage',   label: 'Vintage'   },
  { value: 'commander', label: 'Commander' },
  { value: 'pauper',    label: 'Pauper'    },
];

const RARITIES = ['mythic', 'rare', 'uncommon', 'common'];

function resolveImg(path: string | null): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${IMAGE_BASE}/${path}`;
}

function fmt(v: number | null): string {
  if (!v) return '—';
  return `$${Number(v).toFixed(2)}`;
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Hover popup ──────────────────────────────────────────────────────────────
function HoverPopup({ imageUrl, x, y }: { imageUrl: string; x: number; y: number }) {
  const showLeft = x > window.innerWidth * 0.6;
  const top = Math.min(Math.max(y - 80, 10), window.innerHeight - 460);
  return (
    <div style={{
      position: 'fixed', top, zIndex: 9999, pointerEvents: 'none',
      left: showLeft ? x - 276 : x + 16,
      width: 260, borderRadius: 10, overflow: 'hidden',
      boxShadow: '0 24px 60px rgba(0,0,0,0.85)',
      animation: 'wl-pop 0.1s ease',
    }}>
      <img src={imageUrl} alt="" style={{ width: '100%', display: 'block' }} />
    </div>
  );
}

// ── Inline notes editor ──────────────────────────────────────────────────────
function NotesCell({ item, onSave }: { item: WishListItem; onSave: (id: number, notes: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [val,     setVal]     = useState(item.notes || '');
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);
  useEffect(() => { setVal(item.notes || ''); }, [item.notes]);

  const save = () => {
    setEditing(false);
    onSave(item.wishListId, val.trim());
  };

  if (editing) {
    return (
      <textarea
        ref={ref}
        className="wl-notes-input"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === 'Escape') setEditing(false); }}
        rows={2}
        maxLength={500}
      />
    );
  }
  return (
    <span
      className={`wl-notes-text ${!item.notes ? 'empty' : ''}`}
      onClick={() => setEditing(true)}
      title="Click to edit notes"
    >
      {item.notes || '+ Add note'}
    </span>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function WishListPage() {
  const navigate = useNavigate();

  const [items,      setItems]      = useState<WishListItem[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page,       setPage]       = useState(0);
  const [limit,      setLimit]      = useState(48);
  const [view,       setView]       = useState<ViewMode>('list');
  const [sortBy,     setSortBy]     = useState<SortKey>('added_desc');
  const [query,      setQuery]      = useState('');
  const [rarities,   setRarities]   = useState<string[]>([]);
  const [format,     setFormat]     = useState('');
  const [minPrice,   setMinPrice]   = useState('');
  const [maxPrice,   setMaxPrice]   = useState('');
  const [hoverImg,   setHoverImg]   = useState<string | null>(null);
  const [hoverPos,   setHoverPos]   = useState<{ x: number; y: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef    = useRef<AbortController | null>(null);

  const fetchItems = useCallback(async (
    q: string, r: string[], f: string, minP: string, maxP: string,
    sort: SortKey, pg: number, lim: number
  ) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wishlist`, {
        params: {
          q: q.trim(),
          rarities: r.join(','),
          format: f,
          minPrice: minP || 0,
          maxPrice: maxP || 0,
          sortBy: sort,
          page: pg,
          limit: lim,
        },
        signal,
      });
      if (!signal.aborted) {
        setItems(res.data.items);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      }
    } catch (e: any) {
      if (e?.code !== 'ERR_CANCELED') console.error('WishList fetch failed', e);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchItems(query, rarities, format, minPrice, maxPrice, sortBy, page, limit);
    }, query ? 300 : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, rarities, format, minPrice, maxPrice, sortBy, page, limit, fetchItems]);

  const removeItem = async (cardId: string) => {
    try {
      await axios.delete(`${API}/wishlist/${cardId}`);
      fetchItems(query, rarities, format, minPrice, maxPrice, sortBy, page, limit);
    } catch (e) { console.error('Remove failed', e); }
  };

  const saveNotes = async (wishListId: number, notes: string) => {
    try {
      await axios.patch(`${API}/wishlist/${wishListId}`, { notes: notes || null });
      setItems(prev => prev.map(i => i.wishListId === wishListId ? { ...i, notes: notes || null } : i));
    } catch (e) { console.error('Save notes failed', e); }
  };

  const handleContextMenu = (item: WishListItem, e: React.MouseEvent) => {
    e.preventDefault();
    setHoverImg(null); setHoverPos(null);
    setContextMenu({
      x: e.clientX, y: e.clientY,
      items: [
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => navigate(`/cards/${item.cardId}`, { state: { from: '/wishlist' } }),
        },
        {
          icon: '✕',
          label: 'Remove from Wish List',
          onClick: () => removeItem(item.cardId),
          danger: true,
          divider: true,
        },
      ],
    });
  };

  const toggleRarity = (r: string) => {
    setRarities(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
    setPage(0);
  };

  const showHover = (item: WishListItem, e: React.MouseEvent) => {
    const url = resolveImg(item.imageNormal) || resolveImg(item.imageSmall);
    if (url && !contextMenu) { setHoverImg(url); setHoverPos({ x: e.clientX, y: e.clientY }); }
  };
  const moveHover = (e: React.MouseEvent) => { if (hoverImg) setHoverPos({ x: e.clientX, y: e.clientY }); };
  const hideHover = () => { setHoverImg(null); setHoverPos(null); };

  const goToCard = (cardId: string) =>
    navigate(`/cards/${cardId}`, { state: { from: '/wishlist' } });

  return (
    <div className="wl-page">

      {/* ── HEADER ── */}
      <div className="wl-header">
        <h1 className="wl-title">Wish List</h1>
        <span className="wl-count"><strong>{total.toLocaleString()}</strong> cards</span>

        {/* Quick search */}
        <input
          className="wl-search"
          type="text"
          placeholder="Search…"
          value={query}
          onChange={e => { setQuery(e.target.value); setPage(0); }}
        />

        {/* Rarity pills */}
        <div className="wl-rarity-pills">
          {RARITIES.map(r => (
            <button key={r}
              className={`wl-rarity-pill ${rarities.includes(r) ? 'active' : ''}`}
              style={{ '--rc': RARITY_COLORS[r] } as React.CSSProperties}
              onClick={() => toggleRarity(r)}>
              <span className="wl-rarity-dot" style={{ background: RARITY_COLORS[r] }} />
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>

        {/* Format */}
        <select className="wl-select" value={format}
          onChange={e => { setFormat(e.target.value); setPage(0); }}>
          {FORMAT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>

        {/* Price range */}
        <div className="wl-price-range">
          <input className="wl-price-input" type="number" placeholder="Min $"
            value={minPrice} min={0}
            onChange={e => { setMinPrice(e.target.value); setPage(0); }} />
          <span className="wl-price-sep">–</span>
          <input className="wl-price-input" type="number" placeholder="Max $"
            value={maxPrice} min={0}
            onChange={e => { setMaxPrice(e.target.value); setPage(0); }} />
        </div>

        {/* Sort */}
        <select className="wl-select" value={sortBy}
          onChange={e => { setSortBy(e.target.value as SortKey); setPage(0); }}>
          <option value="added_desc">Added: Newest</option>
          <option value="added_asc">Added: Oldest</option>
          <option value="name">Name A–Z</option>
          <option value="name_desc">Name Z–A</option>
          <option value="price_desc">Price ↓</option>
          <option value="price_asc">Price ↑</option>
          <option value="rarity">Rarity</option>
        </select>

        {/* View toggle */}
        <div className="wl-view-toggle">
          <button className={`wl-view-btn ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')} title="List view">≡</button>
          <button className={`wl-view-btn ${view === 'grid' ? 'active' : ''}`}
            onClick={() => setView('grid')} title="Grid view">⊞</button>
        </div>

        {/* Pagination top */}
        {total > 0 && (
          <Pagination page={page} totalPages={totalPages} total={total} limit={limit}
            onPageChange={p => setPage(p)}
            onLimitChange={l => { setLimit(l); setPage(0); }}
            compact />
        )}
      </div>

      {/* ── CONTENT ── */}
      <div className="wl-body">
        {loading && <div className="wl-loading"><div className="wl-spinner" /></div>}

        {!loading && items.length === 0 && (
          <div className="wl-empty">
            <div className="wl-empty-icon">♡</div>
            <div className="wl-empty-text">Your wish list is empty</div>
            <div className="wl-empty-sub">Right-click any card to add it</div>
          </div>
        )}

        {/* LIST VIEW */}
        {view === 'list' && items.length > 0 && (
          <div className="wl-table-wrap">
            <table className="wl-table">
              <thead>
                <tr>
                  <th className="wl-th" style={{ width: 40 }}></th>
                  <th className="wl-th">Card</th>
                  <th className="wl-th">Set</th>
                  <th className="wl-th">Type</th>
                  <th className="wl-th">Mana</th>
                  <th className="wl-th align-right">Price</th>
                  <th className="wl-th">Notes</th>
                  <th className="wl-th">Added</th>
                  <th className="wl-th" style={{ width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.wishListId} className="wl-row"
                    onContextMenu={e => handleContextMenu(item, e)}>
                    <td className="wl-td" style={{ padding: '4px 8px' }}
                      onMouseEnter={e => showHover(item, e)}
                      onMouseMove={moveHover}
                      onMouseLeave={hideHover}>
                      {(item.imageNormal || item.imageSmall) && (
                        <img src={resolveImg(item.imageNormal) || resolveImg(item.imageSmall)!}
                          alt={item.name}
                          className="wl-thumb" onClick={() => goToCard(item.cardId)} />
                      )}
                    </td>
                    <td className="wl-td"
                      onMouseEnter={e => showHover(item, e)}
                      onMouseMove={moveHover}
                      onMouseLeave={hideHover}>
                      <div className="wl-card-name" onClick={() => goToCard(item.cardId)}>
                        {item.name}
                      </div>
                      <div className="wl-card-meta">
                        <span className="wl-rarity-dot-sm"
                          style={{ background: RARITY_COLORS[item.rarity] || '#888' }} />
                        {item.reserved && <span className="wl-rl-badge">RL</span>}
                      </div>
                    </td>
                    <td className="wl-td">
                      <span className="wl-set-code">{item.setCode?.toUpperCase()}</span>
                      <div className="wl-set-name">{item.setName}</div>
                    </td>
                    <td className="wl-td wl-type-line">{item.typeLine}</td>
                    <td className="wl-td"><ManaCost manaCost={item.manaCost} size={13} /></td>
                    <td className="wl-td align-right">
                      <span className="wl-price">{fmt(item.price)}</span>
                    </td>
                    <td className="wl-td wl-notes-cell">
                      <NotesCell item={item} onSave={saveNotes} />
                    </td>
                    <td className="wl-td wl-date">{formatDate(item.addedDate)}</td>
                    <td className="wl-td">
                      <button className="wl-remove-btn" title="Remove from wish list"
                        onClick={() => removeItem(item.cardId)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* GRID VIEW */}
        {view === 'grid' && items.length > 0 && (
          <div className="wl-grid">
            {items.map(item => (
              <div key={item.wishListId} className="wl-tile"
                onClick={() => goToCard(item.cardId)}
                onMouseEnter={e => showHover(item, e)}
                onMouseMove={moveHover}
                onMouseLeave={hideHover}
                onContextMenu={e => handleContextMenu(item, e)}>
                <div className="wl-tile-img-wrap">
                  {item.imageSmall || item.imageNormal
                    ? <img src={resolveImg(item.imageNormal) || resolveImg(item.imageSmall)!} alt={item.name} className="wl-tile-img" />
                    : <div className="wl-tile-placeholder">{item.name}</div>}
                  <button className="wl-tile-remove"
                    onClick={e => { e.stopPropagation(); removeItem(item.cardId); }}
                    title="Remove from wish list">✕</button>
                </div>
                <div className="wl-tile-info">
                  <div className="wl-tile-name">{item.name}</div>
                  <div className="wl-tile-meta">
                    <span className="wl-set-code">{item.setCode?.toUpperCase()}</span>
                    <span className="wl-tile-price">{fmt(item.price)}</span>
                  </div>
                  {item.notes && (
                    <div className="wl-tile-notes" title={item.notes}>{item.notes}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── FOOTER PAGINATION ── */}
      {totalPages > 1 && (
        <div className="wl-footer">
          <Pagination page={page} totalPages={totalPages} total={total} limit={limit}
            onPageChange={p => setPage(p)}
            onLimitChange={l => { setLimit(l); setPage(0); }} />
        </div>
      )}

      {hoverImg && hoverPos && !contextMenu && (
        <HoverPopup imageUrl={hoverImg} x={hoverPos.x} y={hoverPos.y} />
      )}
      {contextMenu && (
        <ContextMenu x={contextMenu.x} y={contextMenu.y}
          items={contextMenu.items} onClose={() => setContextMenu(null)} />
      )}
    </div>
  );
}
